import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import "../css/ChatPage.css";
import send from "../assets/icons/send.png";
import si from "../assets/icons/soundIcon.png";

const AudioToAudio = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachedAudio, setAttachedAudio] = useState(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleAttachClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "audio/mpeg") return;

    setAttachedAudio({
      file,
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    });

    // Reset file input so same file can be selected again
    e.target.value = null;
  };

  const handleSend = () => {
    if (!input.trim() && !attachedAudio) return;

    const time = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const userMsg = {
      id: Date.now(),
      sender: "user",
      message: input || "Uploaded audio file:",
      time,
      ...(attachedAudio && {
        audioFile: {
          name: attachedAudio.name,
          size: attachedAudio.size,
        },
      }),
    };

    const botMsg = {
      id: Date.now() + 1,
      sender: "bot",
      message: "Here’s the result from the audio you uploaded.",
      time,
      ...(attachedAudio && {
        audioFile: {
          name: attachedAudio.name,
          size: attachedAudio.size,
          transcription: "This is a dummy transcription of your audio file.",
        },
      }),
    };

    setMessages((prev) => [...prev, userMsg, botMsg]);
    setInput("");
    setAttachedAudio(null);
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === "bot";
    const profile = isBot
      ? "https://img.freepik.com/free-vector/chatbot-chat-message-vectorart_78370-4104.jpg?semt=ais_hybrid&w=740"
      : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";
    const bubbleClass = isBot ? "bot-bubble" : "user-bubble";
    const containerAlign = isBot ? "flex-row" : "flex-row-reverse";

    return (
      <div
        className={`d-flex ${containerAlign} align-items-start my-3`}
        key={msg.id}
      >
        <div className="d-flex flex-column" style={{ maxWidth: "75%" }}>
          <div className={`chat-bubble ${bubbleClass}`}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <img src={profile} className="chat-avatar" alt="avatar" />
              <strong>{isBot ? "Bot" : "User"}</strong>
              <small className="text-muted">{msg.time}</small>
            </div>
            <div>{msg.message}</div>

            {msg.audioFile && (
              <div className="mt-3 p-2 bg-light w-75 rounded position-relative">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-3">
                    <img src={si} alt="sound icon" />
                    <div>
                      <div className="fw-semibold">{msg.audioFile.name}</div>
                      <div className="text-muted">{msg.audioFile.size}</div>
                    </div>
                  </div>
                  {isBot && (
                    <a
                      href={msg.audioFile.downloadUrl || "#"}
                      download
                      className="text-primary"
                      title="Download Audio"
                    >
                      <i className="bi bi-download fs-5"></i>
                    </a>
                  )}
                </div>
                {isBot && (
                  <div className="text-dark mt-2">
                    {msg.audioFile.transcription}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid mt-3">
      {/* Chat Area */}
      <div className="chat-box">
        {messages.map((msg) => renderMessage(msg))}
        <div ref={chatEndRef} />
      </div>

      {/* Audio Preview above input */}
      {attachedAudio && (
        <div className="position-relative mb-2 bg-light p-3 rounded w-50">
          <div className="position-absolute top-0 end-0 p-2">
            <i className="bi bi-x-lg text-muted" title="Audio selected"></i>
          </div>
          <div className="d-flex align-items-center gap-3">
            <img src={si} alt="sound icon" />
            <div>
              <div className="fw-semibold">{attachedAudio.name}</div>
              <div className="text-muted">{attachedAudio.size}</div>
            </div>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border rounded p-3 mt-2" style={{ minHeight: "120px" }}>
        <div className="mb-1">
          <input
            type="text"
            className="form-control border-0 shadow-none"
            placeholder="Welcome back to VelaKoda! How can I assist you?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />
        </div>
        <hr />
        <div className="d-flex align-items-center justify-content-between p-2 rounded-3">
          <div className="d-flex gap-3">
            <button
              className="btn btn-link text-muted p-0"
              onClick={handleAttachClick}
            >
              <i className="bi bi-paperclip fs-5"></i>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".mp3"
              style={{ display: "none" }}
            />
            
            <button className="btn btn-link text-muted p-0">
              <i className="bi bi-volume-up fs-5"></i>
            </button>
            <button className="btn btn-link text-muted p-0">
              <i className="bi bi-mic fs-5"></i>
            </button>
            <button className="btn btn-link text-muted p-0">
              <GraphicEqIcon fontSize="medium" style={{ color: "#6c757d" }} />
            </button>
          </div>

          <button className="custom-send-btn" onClick={handleSend}>
            <img src={send} alt="send" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-3">
        <p>
          By Messaging, you agree to our <strong>Terms</strong> and have read
          our <strong>Privacy Policy</strong>.
        </p>
      </div>
    </div>
  );
};

export default AudioToAudio;
